import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Users, Plus, Trash2, Save, Loader2, Copy, Gift } from "lucide-react";

/* -------- Group Ownership -------- */
const emptyCo = () => ({ name: "", id_number: "", share_pct: 0, contact: "" });

export function GroupSharePanel() {
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState("");
  const [coOwners, setCoOwners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/log/group-shares");
      setItems(data.shares || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (i, k, v) => setCoOwners(coOwners.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addRow = () => setCoOwners([...coOwners, emptyCo()]);
  const removeRow = (i) => setCoOwners(coOwners.filter((_, idx) => idx !== i));

  const totalPct = coOwners.reduce((a, c) => a + (parseFloat(c.share_pct) || 0), 0);

  const submit = async () => {
    if (!coOwners.length) return toast.error("Add at least one co-owner.");
    if (totalPct !== 100) return toast.error(`Shares must total 100% (currently ${totalPct}%).`);
    setSaving(true);
    try {
      await api.post("/log/group-shares", { group_name: group, co_owners: coOwners });
      toast.success("Group ownership record saved.");
      setGroup(""); setCoOwners([]);
      await load();
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/log/group-shares/${id}`); await load(); };

  if (loading) return null;

  return (
    <div className="mv-card p-6" data-testid="group-share-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <Users size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Group Ownership</span></div>
          <div className="font-display text-lg mv-green-text">Family / friends / community — every share on the deed.</div>
        </div>
      </div>

      <label className="block mt-2">
        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Group name</span>
        <input value={group} onChange={(e) => setGroup(e.target.value)}
          placeholder="e.g. Moyo Family Circle" className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
      </label>

      <div className="mt-3 space-y-2" data-testid="coowners-editor">
        {coOwners.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <input value={c.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Full name"
              className="col-span-12 md:col-span-4 px-3 py-2 rounded-xl border mv-hairline text-[13px]" data-testid={`co-name-${i}`}/>
            <input value={c.id_number} onChange={(e) => update(i, "id_number", e.target.value)} placeholder="ID / Passport"
              className="col-span-6 md:col-span-3 px-3 py-2 rounded-xl border mv-hairline text-[13px]"/>
            <input value={c.contact} onChange={(e) => update(i, "contact", e.target.value)} placeholder="Contact"
              className="col-span-6 md:col-span-3 px-3 py-2 rounded-xl border mv-hairline text-[13px]"/>
            <input type="number" min="0" max="100" value={c.share_pct}
              onChange={(e) => update(i, "share_pct", parseFloat(e.target.value) || 0)} placeholder="%"
              className="col-span-4 md:col-span-1 px-3 py-2 rounded-xl border mv-hairline text-[13px]" data-testid={`co-share-${i}`}/>
            <button onClick={() => removeRow(i)}
              className="col-span-2 md:col-span-1 p-2 text-red-600 hover:bg-red-50 rounded-xl" aria-label="Remove">
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={addRow} className="btn-ghost !py-2" data-testid="co-add"><Plus size={14}/> Add co-owner</button>
        <div className="text-[13px]" style={{ color: coOwners.length && totalPct !== 100 ? "#B33A3A" : "var(--mv-muted)" }}>
          Total: <b>{totalPct}%</b>
        </div>
        <button onClick={submit} disabled={saving} className="btn-green ml-auto" data-testid="co-save">
          {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
          {saving ? "Saving…" : "Save Group"}
        </button>
      </div>

      {/* Saved groups */}
      {items.length > 0 && (
        <div className="mt-6 pt-5 border-t mv-hairline">
          <div className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: "var(--mv-muted)" }}>Saved groups</div>
          <ul className="space-y-3" data-testid="saved-groups">
            {items.map((g) => (
              <li key={g.id} className="flex justify-between items-start gap-3 border-b mv-hairline pb-3">
                <div>
                  <div className="font-semibold mv-green-text">{g.group_name || "Untitled group"}</div>
                  <ul className="mt-1 text-[13px]" style={{ color: "var(--mv-muted)" }}>
                    {(g.co_owners || []).map((c, i) => <li key={i}>{c.name} · {c.share_pct}%</li>)}
                  </ul>
                </div>
                <button onClick={() => remove(g.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={16}/></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* -------- Referrals -------- */
export function ReferralPanel() {
  const [code, setCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/log/referrals");
        setCode(data.referral_code);
        setReferrals(data.referrals || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const shareUrl = code ? `${window.location.origin}/register-interest?ref=${code}` : "";
  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Referral link copied."); };

  if (loading) return null;

  return (
    <div className="mv-card p-6" data-testid="referral-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <Gift size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Your Referral Programme</span></div>
          <div className="font-display text-lg mv-green-text">Bring family. Save fees together.</div>
        </div>
      </div>
      <p className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
        Share your personal link. When someone joins MAVULA via your link, both accounts qualify for reduced admin fees on your next registration.
      </p>

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Your referral code</div>
        <div className="font-display text-2xl mv-green-text mt-1" data-testid="referral-code">{code}</div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input readOnly value={shareUrl} className="flex-1 min-w-0 px-3 py-2 rounded-xl border mv-hairline text-[13px] bg-black/5" />
        <button onClick={copy} className="btn-green" data-testid="referral-copy"><Copy size={14}/> Copy link</button>
      </div>

      <div className="mt-5 pt-4 border-t mv-hairline">
        <div className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--mv-muted)" }}>People you've referred</div>
        {referrals.length === 0 ? (
          <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>No referrals yet — share your link!</div>
        ) : (
          <ul className="text-[13.5px] space-y-1" data-testid="referral-list">
            {referrals.map((r) => (
              <li key={r.id} className="flex justify-between border-b mv-hairline pb-1">
                <span>{r.lead_email}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>{r.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
